import { api } from "./api";
import type { AudienceParams } from "./audienceFilter";

/** A company placed on the map, with everything its marker card shows. */
export interface MapPoint {
  regCode: string;
  name: string;
  lat: number;
  lng: number;
  county: string | null;
  addressFull: string | null;
  emtakText: string | null;
  webUrl: string | null;
  email: string | null;
  phone: string | null;
  /** Cents, as a string: it is a BigInt on the server and JSON has no such thing. */
  revenueCents: string | null;
  employees: number | null;
  /** 0-100 web need, or null where the recompute pass has not reached this row. */
  webNeedScore: number | null;
}

/** A bubble standing in for the companies in one grid cell. */
export interface MapCell {
  lat: number;
  lng: number;
  n: number;
  /** How many of them have never told the register about a website. */
  noWeb: number;
  /** Mean web need over the scored rows in this cell. Null when none of them
   *  carries a score yet, which is not the same as a cell full of zeros. */
  avgNeed: number | null;
}

export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface MapResult {
  mode: "points" | "cells";
  /** Real matches in view, not the number of markers sent. */
  total: number;
  points: MapPoint[];
  cells: MapCell[];
  /** Above this many matches the server clusters. Sent so the UI does not keep
   *  its own copy of a number that lives on the server. */
  pointLimit: number;
}

/**
 * The viewport, filtered.
 *
 * Takes a signal because this fires on every pan and zoom: a request the
 * operator has already scrolled past is worth cancelling, and without it the
 * answers can land out of order and redraw the map with an older viewport.
 */
export function getMapCompanies(
  bounds: MapBounds,
  zoom: number,
  filter: AudienceParams,
  signal?: AbortSignal,
): Promise<MapResult> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v !== undefined && v !== "" && v !== null) qs.set(k, String(v));
  }
  // Six decimals is under a tenth of a metre. Rounding keeps the URL readable
  // and, more usefully, makes two nearly identical viewports produce the same
  // request so the browser cache can answer the second one.
  qs.set("north", bounds.north.toFixed(6));
  qs.set("south", bounds.south.toFixed(6));
  qs.set("east", bounds.east.toFixed(6));
  qs.set("west", bounds.west.toFixed(6));
  qs.set("zoom", String(Math.round(zoom)));
  return api.get<MapResult>(`/admin/crm/maps/companies?${qs.toString()}`, signal);
}

/** How far the address-to-coordinate pass has got. */
export interface GeocodeProgress {
  withAddress: number;
  placed: number;
  missed: number;
  pending: number;
  running: boolean;
  doneThisRun: number;
  lastError: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}

export const getGeocodeProgress = (): Promise<GeocodeProgress> =>
  api.get<GeocodeProgress>("/admin/crm/maps/geocode");

export const startGeocode = (): Promise<{ started: boolean; alreadyRunning: boolean }> =>
  api.post("/admin/crm/maps/geocode/start", {});

export const stopGeocode = (): Promise<{ ok: boolean }> =>
  api.post("/admin/crm/maps/geocode/stop", {});

/**
 * What Google says about one company, right now.
 *
 * ⚠ Fetched on demand and held nowhere. Google's Places policy allows the place
 * id to be stored indefinitely and nothing else - a rating has to be requested
 * live every time. That is why there is no rating on `MapPoint` and no way to
 * filter the register by one: a filter needs a column, and a column is the
 * warehousing the policy forbids.
 */
export type CompanyPlace =
  | {
      ok: true;
      placeId: string;
      /** 1.0 to 5.0, or null when Google knows the place but nobody rated it. */
      rating: number | null;
      reviewCount: number | null;
      /** Where the reader goes to read the reviews. Required by the policy. */
      googleMapsUri: string | null;
    }
  | { ok: false; reason: "no_key" | "not_found" | "error" };

export const getCompanyPlace = (regCode: string, signal?: AbortSignal): Promise<CompanyPlace> =>
  api.get<CompanyPlace>(`/admin/crm/maps/places/${encodeURIComponent(regCode)}`, signal);
